import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { useGlobalStore } from "~/stores/store-provider";
import { fetchInstagramAdAccounts } from "~/lib/network/database-connection";
import {
  FaArrowLeft,
  FaInfoCircle,
  FaKey,
  FaUser,
  FaHashtag,
} from "react-icons/fa";
import { SiInstagram } from "react-icons/si";
import { RiRefreshLine } from "react-icons/ri";

type InstagramFormData = {
  name: string;
  access_token: string;
  instagram_business_account_id: string;
  facebook_page_id: string;
  ad_account_ids: string[];
};

type AdAccount = {
  id: string;
  name: string;
  status?: number;
};

const InstagramConnection = () => {
  const navigate = useNavigate();
  const { addConnection } = useGlobalStore((state) => state);
  const timersRef = useRef<Set<NodeJS.Timeout>>(new Set());

  const [formData, setFormData] = useState<InstagramFormData>({
    name: "",
    access_token: "",
    instagram_business_account_id: "",
    facebook_page_id: "",
    ad_account_ids: [],
  });

  const [nameError, setNameError] = useState<string>("");
  const [tokenError, setTokenError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [adAccounts, setAdAccounts] = useState<AdAccount[]>([]);
  const [selectedAdAccounts, setSelectedAdAccounts] = useState<string[]>([]);
  const [isFetchingAccounts, setIsFetchingAccounts] = useState(false);
  const [accountsFetched, setAccountsFetched] = useState(false);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const handleNameChange = (value: string) => {
    setFormData({
      ...formData,
      name: value,
    });

    if (value.trim()) {
      setNameError("");
    }
  };

  const handleTokenChange = (value: string) => {
    setFormData({
      ...formData,
      access_token: value,
    });

    if (value.trim()) {
      setTokenError("");
    }

    // Reset accounts when token changes
    setAccountsFetched(false);
    setAdAccounts([]);
    setSelectedAdAccounts([]);
  };

  const handleFetchAccounts = async () => {
    if (!formData.access_token.trim()) {
      setTokenError("Access token is required");
      return;
    }

    setIsFetchingAccounts(true);
    try {
      const data = await fetchInstagramAdAccounts(formData.access_token);
      setAdAccounts(data?.ad_accounts || []);
      setAccountsFetched(true);
    } catch (error) {
      setToastType("error");
      setToastMessage(
        "Failed to fetch ad accounts. Please check your access token."
      );
      setShowToast(true);

      const timer = setTimeout(() => {
        setShowToast(false);
        timersRef.current.delete(timer);
      }, 2000);
      timersRef.current.add(timer);
    } finally {
      setIsFetchingAccounts(false);
    }
  };

  const handleAdAccountToggle = (accountId: string) => {
    setSelectedAdAccounts((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name.trim()) {
      setNameError("Connection name is required");
      return;
    }

    if (!formData.access_token.trim()) {
      setTokenError("Access token is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 60);

      await addConnection({
        type: "instagram",
        connection_name: formData.name,
        instagram_business_account_id: formData.instagram_business_account_id,
        facebook_page_id: formData.facebook_page_id,
        access_token: formData.access_token,
        ad_account_ids:
          selectedAdAccounts.length > 0 ? selectedAdAccounts : undefined,
        token_expiry: expiryDate.toISOString(),
      });

      // Show success toast
      setToastType("success");
      setToastMessage("Connection saved successfully!");
      setShowToast(true);

      // Navigate back after a short delay
      const timer = setTimeout(() => {
        navigate("/connections");
        timersRef.current.delete(timer);
      }, 1500);
      timersRef.current.add(timer);
    } catch (error) {
      console.error("Failed to save connection:", error);

      // Show error toast
      setToastType("error");
      setToastMessage("Failed to save connection. Please try again.");
      setShowToast(true);

      const timer = setTimeout(() => {
        setShowToast(false);
        timersRef.current.delete(timer);
      }, 2000);
      timersRef.current.add(timer);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full bg-base-200/50 overflow-y-auto">
      <div className="container mx-auto px-4 py-8 max-w-2xl min-h-full">
        {/* Back Navigation */}
        <button
          onClick={() => navigate("/connections")}
          className="btn btn-ghost btn-sm gap-2 mb-6"
        >
          <FaArrowLeft className="w-3 h-3" />
          Back to Connections
        </button>

        {/* Main Card */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body p-8">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-base-200">
              <div className="p-3 bg-pink-500/10 rounded-lg">
                <SiInstagram className="w-7 h-7 text-pink-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Connect Instagram</h1>
                <p className="text-sm text-base-content/60">
                  Configure your Instagram business account integration
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Connection Name */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">
                    Connection Name
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g., Instagram Business"
                  className={`input input-bordered w-full ${
                    nameError ? "input-error" : ""
                  }`}
                />
                {nameError && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {nameError}
                    </span>
                  </label>
                )}
              </div>

              {/* Access Token */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">Access Token</span>
                  <div
                    className="tooltip"
                    data-tip="Instagram Graph API access token"
                  >
                    <FaInfoCircle className="w-3 h-3 text-base-content/50" />
                  </div>
                </label>
                <label className="input input-bordered flex items-center gap-2 w-full">
                  <FaKey className="w-4 h-4 text-base-content/50" />
                  <input
                    type="password"
                    value={formData.access_token}
                    onChange={(e) => handleTokenChange(e.target.value)}
                    placeholder="Enter your Instagram access token"
                    className={`grow ${tokenError ? "text-error" : ""}`}
                  />
                </label>
                {tokenError && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {tokenError}
                    </span>
                  </label>
                )}
              </div>

              {/* Optional Fields */}
              <div className="space-y-4">
                <div className="divider text-xs">Optional Fields</div>

                {/* Business Account ID */}
                <div className="form-control w-full">
                  <label className="label mb-2">
                    <span className="label-text font-medium">
                      Business Account ID
                    </span>
                    <div
                      className="tooltip"
                      data-tip="For organic content access"
                    >
                      <FaInfoCircle className="w-3 h-3 text-base-content/50" />
                    </div>
                  </label>
                  <label className="input input-bordered flex items-center gap-2 w-full">
                    <FaUser className="w-4 h-4 text-base-content/50" />
                    <input
                      type="text"
                      value={formData.instagram_business_account_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          instagram_business_account_id: e.target.value,
                        })
                      }
                      placeholder="Instagram Business Account ID"
                      className="grow"
                    />
                  </label>
                </div>

                {/* Facebook Page ID */}
                <div className="form-control w-full">
                  <label className="label mb-2">
                    <span className="label-text font-medium">
                      Facebook Page ID
                    </span>
                    <div
                      className="tooltip"
                      data-tip="Associated Facebook Page"
                    >
                      <FaInfoCircle className="w-3 h-3 text-base-content/50" />
                    </div>
                  </label>
                  <label className="input input-bordered flex items-center gap-2 w-full">
                    <FaHashtag className="w-4 h-4 text-base-content/50" />
                    <input
                      type="text"
                      value={formData.facebook_page_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          facebook_page_id: e.target.value,
                        })
                      }
                      placeholder="Facebook Page ID"
                      className="grow"
                    />
                  </label>
                </div>

                {/* Ad Accounts Section */}
                {formData.access_token && (
                  <div className="form-control w-full">
                    <label className="label mb-2">
                      <span className="label-text font-medium">
                        Ad Accounts
                      </span>
                    </label>
                    {!accountsFetched ? (
                      <button
                        type="button"
                        onClick={handleFetchAccounts}
                        disabled={isFetchingAccounts}
                        className="btn btn-outline btn-sm flex gap-2"
                      >
                        {isFetchingAccounts ? (
                          <>
                            <span className="loading loading-spinner loading-xs"></span>
                            Fetching Accounts...
                          </>
                        ) : (
                          <>
                            <RiRefreshLine className="w-4 h-4" />
                            Fetch Ad Accounts
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {adAccounts.length > 0 ? (
                          adAccounts.map((account) => (
                            <label
                              key={account.id}
                              className="flex items-center gap-3 p-2 rounded hover:bg-base-200 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="checkbox checkbox-primary checkbox-sm"
                                checked={selectedAdAccounts.includes(
                                  account.id
                                )}
                                onChange={() =>
                                  handleAdAccountToggle(account.id)
                                }
                              />
                              <span className="text-sm">{account.name}</span>
                              <span className="text-xs text-base-content/50">
                                ({account.id})
                              </span>
                            </label>
                          ))
                        ) : (
                          <p className="text-sm text-base-content/60 text-info">
                            No ad accounts found
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-base-200">
                <button
                  type="button"
                  onClick={() => navigate("/connections")}
                  className="btn btn-ghost"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    isSubmitting || !formData.name || !formData.access_token
                  }
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-xs"></span>
                      Saving...
                    </>
                  ) : (
                    "Save Connection"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Toast */}
        {showToast && (
          <div className="toast toast-bottom toast-center z-50">
            <div
              className={`alert ${
                toastType === "success" ? "alert-success" : "alert-error"
              } shadow-lg`}
            >
              <span>{toastMessage}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstagramConnection;
